import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  strapiGetLaboratoryWorkOrderForm,
  strapiReviewLaboratoryWorkOrderParameter,
  strapiSaveLaboratoryWorkOrder,
  type LaboratoryResultBeritaAcaraEntry,
  type LaboratoryResultParameterReviewStatus,
  type LaboratoryResultWorkOrderFormData,
  type StrapiLaboratoryResultGridRow,
} from '../../../api/strapiCrm';
import type { StrapiUser } from '../../../api/strapi';
import { useAuth } from '../../../auth/AuthContext';
import { ChemParameterLabel } from '../../../components/ChemInline';
import { queryKeys } from '../../../query/queryKeys';
import { SecondaryDataHtmlDialog } from './SecondaryDataHtmlDialog';
import { secondaryDataSpec } from './secondaryDataHtml';
import {
  analisTypeListMatches,
  isAdminAnalisLabUser,
  lockedAnalisTypeFilter,
} from '../laboratoryResultGridRows';

export type LaboratoryResultEditMode = 'workOrder' | 'parameter';

export type LaboratoryResultEditDialogProps = {
  open: boolean;
  mode: LaboratoryResultEditMode;
  row: StrapiLaboratoryResultGridRow | null;
  /** Grid rows sharing the same parameter (parameter mode). */
  relatedRows?: StrapiLaboratoryResultGridRow[];
  onClose: () => void;
  onSaved: (workOrderDocumentIds: string[]) => void;
};

type ResultKey = string;

type ParamMeta = {
  work_order_document_id: string;
  berita_acara_document_id: string;
  parameter_uji_document_id: string;
  parameter_name: string;
  parameter_simbol: string;
  review_status: LaboratoryResultParameterReviewStatus;
  saved_result: string;
  saved_result_corrected: string;
};

function resultKey(baDocId: string, paramDocId: string): ResultKey {
  return `${baDocId}::${paramDocId}`;
}

function parseResultKey(key: ResultKey): { baDocId: string; paramDocId: string } {
  const idx = key.indexOf('::');
  if (idx < 0) return { baDocId: key, paramDocId: '' };
  return { baDocId: key.slice(0, idx), paramDocId: key.slice(idx + 2) };
}

function initResultsFromBeritaAcara(
  entries: LaboratoryResultBeritaAcaraEntry[],
  field: 'result' | 'result_corrected',
): Record<ResultKey, string> {
  const out: Record<ResultKey, string> = {};
  for (const ba of entries) {
    for (const p of ba.parameters) {
      const key = resultKey(ba.berita_acara_document_id, p.parameter_uji_document_id);
      const saved = p[field] ?? '';
      // Hasil uji kosong pada parameter lapangan memakai nilai lapangan sebagai default, tetap bisa diedit.
      if (field === 'result' && !saved.trim() && p.is_parameter_lapangan) {
        out[key] = p.parameter_lapangan_value ?? '';
      } else {
        out[key] = saved;
      }
    }
  }
  return out;
}

function initFieldParametersFromBeritaAcara(
  entries: LaboratoryResultBeritaAcaraEntry[],
): Record<ResultKey, string> {
  const out: Record<ResultKey, string> = {};
  for (const ba of entries) {
    for (const p of ba.parameters) {
      if (!p.is_parameter_lapangan) continue;
      out[resultKey(ba.berita_acara_document_id, p.parameter_uji_document_id)] =
        p.parameter_lapangan_value ?? '';
    }
  }
  return out;
}

function jenisSampelLabel(ba: LaboratoryResultBeritaAcaraEntry, fallback = ''): string {
  return ba.jenis_sampel?.trim() || fallback.trim() || '';
}

function isSpvLaboratorium(user: StrapiUser | null | undefined): boolean {
  const code = user?.userTypeCode?.trim().toLowerCase() ?? '';
  return code === 'spv_laboratorium' || code === 'superadmin';
}

function reviewChipColor(
  status: LaboratoryResultParameterReviewStatus,
): 'default' | 'success' | 'error' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

function reviewChipLabel(status: LaboratoryResultParameterReviewStatus): string {
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
}

function SecondaryDataButton({
  lhuGroupedName,
  testId,
  onClick,
  inline = false,
}: {
  lhuGroupedName?: string | null;
  testId: string;
  onClick: () => void;
  /** Sit on the same row as the GL label and analis type chip. */
  inline?: boolean;
}) {
  const spec = secondaryDataSpec(lhuGroupedName);
  if (!spec) return null;
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      data-testid={testId}
      sx={
        inline
          ? { py: 0.25, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.2 }
          : { alignSelf: 'flex-start', mt: 0.75, mb: 0.25, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.3 }
      }
    >
      {spec.buttonLabel}
    </Button>
  );
}

function ApprovedResultsSection({
  sectionKey,
  expanded,
  onToggle,
  children,
}: {
  sectionKey: string;
  expanded: boolean;
  onToggle: (sectionKey: string) => void;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(sectionKey)}
        data-testid={`laboratory-result-approved-toggle-${sectionKey}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          m: 0,
          py: 0.75,
          px: 0.5,
          border: 0,
          borderRadius: 1,
          bgcolor: 'action.hover',
          color: 'text.primary',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="body2" component="span">
          approved results
        </Typography>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={1.5} sx={{ pt: 1.25 }}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

function ParameterLabel({
  name,
  simbol,
  focused,
  reviewStatus,
}: {
  name: string;
  simbol?: string;
  focused?: boolean;
  reviewStatus: LaboratoryResultParameterReviewStatus;
}) {
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: focused ? 600 : 400,
          '& .katex-html': { whiteSpace: 'normal' },
          '& sub, & sup': { fontSize: '0.75em', lineHeight: 0 },
        }}
      >
        <ChemParameterLabel nama={name} simbol={simbol} />
      </Typography>
      <Chip
        size="small"
        sx={{ mt: 0.5 }}
        color={reviewChipColor(reviewStatus)}
        label={reviewChipLabel(reviewStatus)}
      />
    </Box>
  );
}

function ResultFieldWithReviewActions({
  fieldKey,
  label,
  value,
  focused,
  reviewStatus,
  canSpvReview,
  busy,
  onChange,
  showCorrected,
  correctedValue,
  onCorrectedChange,
  fieldParameter,
  onFieldParameterChange,
  onApprove,
  onRejectOrRequestEdit,
}: {
  fieldKey: string;
  label: React.ReactNode;
  value: string;
  focused?: boolean;
  reviewStatus: LaboratoryResultParameterReviewStatus;
  canSpvReview: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  showCorrected?: boolean;
  correctedValue?: string;
  onCorrectedChange?: (value: string) => void;
  /** Nilai parameter lapangan di samping hasil uji. `readOnly` untuk modal aksi SPV lab. */
  fieldParameter?: { value: string; readOnly: boolean } | null;
  onFieldParameterChange?: (value: string) => void;
  onApprove: () => void;
  onRejectOrRequestEdit: () => void;
}) {
  const approved = reviewStatus === 'approved';
  const hasResult = Boolean(value.trim());
  const showApprove = canSpvReview && !approved && hasResult;
  const showRequestEdit = canSpvReview && approved;
  const showReject = canSpvReview && !approved && hasResult;
  const corrected = Boolean(showCorrected);
  const showFieldParameter = fieldParameter != null;
  const resultColumns = ['minmax(0, 1.1fr)', 'minmax(0, 1fr)'];
  if (showFieldParameter) resultColumns.push('minmax(0, 1fr)');
  if (corrected) resultColumns.push('minmax(0, 1fr)');
  if (canSpvReview) resultColumns.push('auto');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: resultColumns.join(' '),
        },
        gap: 1.5,
        alignItems: 'end',
        p: focused ? 1 : 0,
        borderRadius: 1,
        bgcolor: focused ? 'action.hover' : 'transparent',
      }}
    >
      {label}
      <TextField
        variant="standard"
        size="small"
        fullWidth
        label="Hasil uji"
        placeholder="Isi hasil"
        autoFocus={focused && !approved}
        value={value}
        disabled={approved || busy}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{
          'data-testid': `laboratory-result-edit-result-${fieldKey}`,
        }}
      />
      {showFieldParameter ? (
        <TextField
          variant="standard"
          size="small"
          fullWidth
          label="Parameter lapangan"
          placeholder="—"
          value={fieldParameter.value}
          disabled={fieldParameter.readOnly || approved || busy}
          onChange={(e) => onFieldParameterChange?.(e.target.value)}
          inputProps={{
            'data-testid': `laboratory-result-edit-field-parameter-${fieldKey}`,
            readOnly: fieldParameter.readOnly,
          }}
        />
      ) : null}
      {corrected ? (
        <TextField
          variant="standard"
          size="small"
          fullWidth
          label="Corrected result"
          placeholder="Isi corrected result"
          value={correctedValue ?? ''}
          disabled={approved || busy}
          onChange={(e) => onCorrectedChange?.(e.target.value)}
          inputProps={{
            'data-testid': `laboratory-result-edit-result-corrected-${fieldKey}`,
          }}
        />
      ) : null}
      {canSpvReview ? (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ pb: 0.25 }}>
          {showApprove ? (
            <Tooltip title="Approve parameter">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={busy}
                  aria-label="Approve parameter"
                  data-testid={`laboratory-result-edit-approve-${fieldKey}`}
                  onClick={onApprove}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {showRequestEdit ? (
            <Tooltip title="Request edit — buka kembali isian (reject)">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={busy}
                  aria-label="Request edit parameter"
                  data-testid={`laboratory-result-edit-request-edit-${fieldKey}`}
                  onClick={onRejectOrRequestEdit}
                >
                  <LockOpenOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {showReject ? (
            <Tooltip title="Reject parameter (request edit)">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={busy}
                  aria-label="Reject parameter"
                  data-testid={`laboratory-result-edit-reject-${fieldKey}`}
                  onClick={onRejectOrRequestEdit}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

export function LaboratoryResultEditDialog({
  open,
  mode,
  row,
  relatedRows = [],
  onClose,
  onSaved,
}: LaboratoryResultEditDialogProps) {
  const { jwt, user } = useAuth();
  const lockedAnalisType = lockedAnalisTypeFilter(user);
  const canSpvReview = isSpvLaboratorium(user);
  const canEditFieldParameter = mode === 'parameter' && (isAdminAnalisLabUser(user) || canSpvReview);
  const showFieldParameter = canEditFieldParameter || (mode === 'workOrder' && canSpvReview);
  const queryClient = useQueryClient();
  const focusParamId = row?.parameter_uji_document_id?.trim() ?? '';
  const focusBaDocId = row?.berita_acara_document_id?.trim() ?? '';
  const anchorJenisSampel = row?.jenis_sampel?.trim() ?? '';

  const workOrderIds = useMemo(() => {
    if (!open || !row) return [] as string[];
    if (mode === 'workOrder') {
      const id = row.work_order_document_id?.trim();
      return id ? [id] : [];
    }
    const ids = new Set<string>();
    for (const r of relatedRows.length ? relatedRows : [row]) {
      const id = r.work_order_document_id?.trim();
      if (id) ids.add(id);
    }
    return [...ids].sort();
  }, [mode, open, relatedRows, row]);

  const workOrderIdsKey = workOrderIds.join(',');

  const formQueries = useQueries({
    queries: workOrderIds.map((woDocId) => ({
      queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId),
      queryFn: () => strapiGetLaboratoryWorkOrderForm(jwt!, woDocId),
      enabled: Boolean(open && jwt && woDocId),
    })),
  });

  const formsDataUpdatedAtKey = formQueries.map((q) => q.dataUpdatedAt).join('|');

  const formsByWo = useMemo(() => {
    const map = new Map<string, LaboratoryResultWorkOrderFormData>();
    workOrderIds.forEach((woDocId, i) => {
      const data = formQueries[i]?.data;
      if (data) map.set(woDocId, data);
    });
    return map;
  }, [workOrderIdsKey, formsDataUpdatedAtKey]);

  const formsPending = formQueries.some((q) => q.isPending);
  const formsError = formQueries.find((q) => q.isError)?.error ?? null;
  const formsReady =
    !formsPending && !formsError && workOrderIds.length > 0 && formsByWo.size === workOrderIds.length;

  const paramMetaByKey = useMemo(() => {
    const map = new Map<ResultKey, ParamMeta>();
    for (const form of formsByWo.values()) {
      for (const ba of form.berita_acara) {
        for (const p of ba.parameters) {
          map.set(resultKey(ba.berita_acara_document_id, p.parameter_uji_document_id), {
            work_order_document_id: form.work_order_document_id,
            berita_acara_document_id: ba.berita_acara_document_id,
            parameter_uji_document_id: p.parameter_uji_document_id,
            parameter_name: p.parameter_name,
            parameter_simbol: p.parameter_simbol ?? '',
            review_status: p.review_status ?? 'pending',
            saved_result: p.result ?? '',
            saved_result_corrected: p.result_corrected ?? '',
          });
        }
      }
    }
    return map;
  }, [formsByWo]);

  const [results, setResults] = useState<Record<ResultKey, string>>({});
  const [correctedResults, setCorrectedResults] = useState<Record<ResultKey, string>>({});
  const [fieldParameterValues, setFieldParameterValues] = useState<Record<ResultKey, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{
    key: ResultKey;
    mode: 'reject' | 'request_edit';
    paramName: string;
  } | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [secondaryTarget, setSecondaryTarget] = useState<{
    workOrderDocumentId: string;
    beritaAcaraDocumentId: string;
    lhuGroupedName: string;
    html: string;
  } | null>(null);

  const resultsRef = useRef(results);
  resultsRef.current = results;
  const correctedResultsRef = useRef(correctedResults);
  correctedResultsRef.current = correctedResults;
  const formsByWoRef = useRef(formsByWo);
  formsByWoRef.current = formsByWo;
  const paramMetaByKeyRef = useRef(paramMetaByKey);
  paramMetaByKeyRef.current = paramMetaByKey;
  const lastResultsInitKeyRef = useRef('');

  useEffect(() => {
    if (!open) {
      lastResultsInitKeyRef.current = '';
      setResults({});
      setCorrectedResults({});
      setFieldParameterValues({});
      setSaveError(null);
      setActionError(null);
      setActionSuccess(null);
      setRejectDialog(null);
      setRejectNote('');
      setExpandedSections({});
      return;
    }
    if (!formsReady) return;
    // Init once per open/session of WO set — refresh after approve/reject keeps typed values.
    const initKey = `${mode}:${workOrderIdsKey}`;
    if (lastResultsInitKeyRef.current === initKey) return;
    lastResultsInitKeyRef.current = initKey;
    const allBa = [...formsByWo.values()].flatMap((f) => f.berita_acara);
    setResults(initResultsFromBeritaAcara(allBa, 'result'));
    setCorrectedResults(initResultsFromBeritaAcara(allBa, 'result_corrected'));
    setFieldParameterValues(initFieldParametersFromBeritaAcara(allBa));
    setSaveError(null);
    const focusKey = focusBaDocId && focusParamId ? resultKey(focusBaDocId, focusParamId) : '';
    const focusApproved = focusKey
      ? paramMetaByKey.get(focusKey)?.review_status === 'approved'
      : false;
    if (!focusApproved) {
      setExpandedSections({});
      return;
    }
    setExpandedSections(
      mode === 'parameter' ? { parameter: true } : focusBaDocId ? { [focusBaDocId]: true } : {},
    );
  }, [open, formsReady, mode, workOrderIdsKey, formsByWo, focusBaDocId, focusParamId, paramMetaByKey]);

  const handleResultChange = useCallback((key: ResultKey, value: string) => {
    setResults((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCorrectedChange = useCallback((key: ResultKey, value: string) => {
    setCorrectedResults((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFieldParameterChange = useCallback((key: ResultKey, value: string) => {
    setFieldParameterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const workOrderSections = useMemo(() => {
    if (mode !== 'workOrder' || !row) return [];
    const form = formsByWo.get(row.work_order_document_id);
    if (!form) return [];
    return [...form.berita_acara].sort((a, b) => a.point_index - b.point_index);
  }, [formsByWo, mode, row]);

  const parameterEntries = useMemo(() => {
    if (mode !== 'parameter' || !row) return [];
    const sourceRows = relatedRows.length ? relatedRows : [row];
    const entries: {
      key: ResultKey;
      work_order_document_id: string;
      job_number: string;
      point_label: string;
      location_name: string;
      jenis_sampel: string;
      parameter_name: string;
      parameter_simbol: string;
      analis_types: string[];
      review_status: LaboratoryResultParameterReviewStatus;
      is_parameter_lapangan: boolean;
      has_emission_correction: boolean;
      berita_acara_document_id: string;
      lhu_grouped_name: string;
      secondary_data_html: string;
      focused: boolean;
    }[] = [];

    for (const gridRow of sourceRows) {
      const form = formsByWo.get(gridRow.work_order_document_id);
      if (!form) continue;
      const ba = form.berita_acara.find(
        (entry) => entry.berita_acara_document_id === gridRow.berita_acara_document_id,
      );
      if (!ba) continue;
      const param = ba.parameters.find(
        (p) => p.parameter_uji_document_id === gridRow.parameter_uji_document_id,
      );
      if (!param) continue;
      if (
        lockedAnalisType != null &&
        !analisTypeListMatches(param.analis_types, lockedAnalisType)
      ) {
        continue;
      }
      const key = resultKey(ba.berita_acara_document_id, param.parameter_uji_document_id);
      entries.push({
        key,
        work_order_document_id: form.work_order_document_id,
        job_number: form.work_order_job_number || gridRow.work_order_job_number,
        point_label: ba.point_label || form.work_order_job_number,
        location_name: ba.location_name,
        jenis_sampel: jenisSampelLabel(ba, gridRow.jenis_sampel),
        parameter_name: param.parameter_name,
        parameter_simbol: param.parameter_simbol ?? gridRow.parameter_simbol,
        analis_types: param.analis_types ?? [],
        review_status: param.review_status ?? 'pending',
        is_parameter_lapangan: Boolean(param.is_parameter_lapangan),
        has_emission_correction: Boolean(ba.has_emission_correction),
        berita_acara_document_id: ba.berita_acara_document_id,
        lhu_grouped_name: ba.lhu_grouped_name ?? '',
        secondary_data_html: ba.secondary_data_html ?? '',
        focused:
          ba.berita_acara_document_id === focusBaDocId &&
          param.parameter_uji_document_id === focusParamId,
      });
    }

    return entries.sort((a, b) => {
      const job = a.job_number.localeCompare(b.job_number, 'id');
      if (job !== 0) return job;
      return a.point_label.localeCompare(b.point_label, 'id');
    });
  }, [focusBaDocId, focusParamId, formsByWo, lockedAnalisType, mode, relatedRows, row]);

  const invalidateAfterReview = useCallback(
    async (woDocIds: string[]) => {
      for (const woDocId of woDocIds) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, woDocId),
        });
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
    },
    [jwt, queryClient],
  );

  const reviewM = useMutation({
    mutationFn: async (payload: {
      work_order_document_id: string;
      berita_acara_document_id: string;
      parameter_uji_document_id: string;
      action: 'approve' | 'reject';
      review_note?: string;
    }) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      return strapiReviewLaboratoryWorkOrderParameter(jwt, payload.work_order_document_id, {
        berita_acara_document_id: payload.berita_acara_document_id,
        parameter_uji_document_id: payload.parameter_uji_document_id,
        action: payload.action,
        review_note: payload.review_note,
      });
    },
    onSuccess: async (data, variables) => {
      setActionError(null);
      if (variables.action === 'approve') {
        setActionSuccess('Parameter di-approve. Isian terkunci.');
      } else if (data.request_edit_triggered) {
        setActionSuccess(
          'Request edit berhasil. Work order dikembalikan ke Pengujian agar isian bisa diubah.',
        );
      } else {
        setActionSuccess('Parameter di-reject. Isian bisa diedit lagi.');
      }
      setRejectDialog(null);
      setRejectNote('');
      await invalidateAfterReview([variables.work_order_document_id]);
    },
    onError: (e) => {
      setActionSuccess(null);
      setActionError(e instanceof Error ? e.message : 'Gagal menjalankan aksi review.');
    },
  });

  const openRejectOrRequestEdit = useCallback(
    (key: ResultKey, dialogMode: 'reject' | 'request_edit') => {
      const meta = paramMetaByKey.get(key);
      if (!meta) return;
      setActionError(null);
      setActionSuccess(null);
      setRejectDialog({
        key,
        mode: dialogMode,
        paramName: meta.parameter_name,
      });
      setRejectNote('');
    },
    [paramMetaByKey],
  );

  const approveParam = useCallback(
    (key: ResultKey) => {
      const meta = paramMetaByKey.get(key);
      if (!meta) return;
      setActionError(null);
      setActionSuccess(null);
      reviewM.mutate({
        work_order_document_id: meta.work_order_document_id,
        berita_acara_document_id: meta.berita_acara_document_id,
        parameter_uji_document_id: meta.parameter_uji_document_id,
        action: 'approve',
      });
    },
    [paramMetaByKey, reviewM],
  );

  const saveM = useMutation({
    mutationFn: async () => {
      if (!jwt || !row) throw new Error('Data tidak lengkap.');
      const latestResults = resultsRef.current;
      const latestCorrected = correctedResultsRef.current;
      const latestForms = formsByWoRef.current;
      const latestMeta = paramMetaByKeyRef.current;

      const touchedWoIds =
        mode === 'workOrder'
          ? [row.work_order_document_id.trim()].filter(Boolean)
          : [
              ...new Set(
                parameterEntries
                  .map((e) => e.work_order_document_id.trim())
                  .filter(Boolean),
              ),
            ];

      if (!touchedWoIds.length) throw new Error('Tidak ada job number terkait untuk disimpan.');

      for (const woDocId of touchedWoIds) {
        const form = latestForms.get(woDocId);
        if (!form) throw new Error(`Form work order ${woDocId} tidak ditemukan.`);

        const approvedByUserId = form.approved_by_user_id;
        const keepReady =
          form.status === 'ready' && approvedByUserId != null && approvedByUserId >= 1;

        const resultForParam = (baDocId: string, paramDocId: string, fallback: string) => {
          const key = resultKey(baDocId, paramDocId);
          const meta = latestMeta.get(key);
          if (meta?.review_status === 'approved') return meta.saved_result.trim();
          return (latestResults[key] ?? fallback).trim();
        };
        const correctedForParam = (baDocId: string, paramDocId: string, fallback: string) => {
          const key = resultKey(baDocId, paramDocId);
          const meta = latestMeta.get(key);
          if (meta?.review_status === 'approved') return meta.saved_result_corrected.trim();
          return (latestCorrected[key] ?? fallback).trim();
        };

        let beritaAcaraPayload: {
          berita_acara_document_id: string;
          items: { parameter_uji_document_id: string; result: string; result_corrected: string }[];
        }[];

        if (mode === 'workOrder') {
          beritaAcaraPayload = form.berita_acara.map((ba) => ({
            berita_acara_document_id: ba.berita_acara_document_id,
            items: ba.parameters.map((p) => ({
              parameter_uji_document_id: p.parameter_uji_document_id,
              result: resultForParam(ba.berita_acara_document_id, p.parameter_uji_document_id, ''),
              result_corrected: ba.has_emission_correction
                ? correctedForParam(ba.berita_acara_document_id, p.parameter_uji_document_id, '')
                : '',
            })),
          }));
        } else {
          const editedBaIds = new Set(
            parameterEntries
              .filter((e) => e.work_order_document_id === woDocId)
              .map((e) => parseResultKey(e.key).baDocId),
          );
          beritaAcaraPayload = form.berita_acara
            .filter((ba) => editedBaIds.has(ba.berita_acara_document_id))
            .map((ba) => ({
              berita_acara_document_id: ba.berita_acara_document_id,
              items: ba.parameters.map((p) => ({
                parameter_uji_document_id: p.parameter_uji_document_id,
                result: resultForParam(
                  ba.berita_acara_document_id,
                  p.parameter_uji_document_id,
                  p.result ?? '',
                ),
                result_corrected: ba.has_emission_correction
                  ? correctedForParam(
                      ba.berita_acara_document_id,
                      p.parameter_uji_document_id,
                      p.result_corrected ?? '',
                    )
                  : '',
              })),
            }));
        }

        if (!beritaAcaraPayload.length) {
          throw new Error(`Tidak ada berita acara untuk disimpan pada ${form.work_order_job_number || woDocId}.`);
        }

        await strapiSaveLaboratoryWorkOrder(jwt, woDocId, {
          status: keepReady ? 'ready' : 'draft',
          ...(keepReady && approvedByUserId != null ? { approved_by_user_id: approvedByUserId } : {}),
          berita_acara: beritaAcaraPayload,
        });
      }

      return touchedWoIds;
    },
    onSuccess: (woIds) => {
      setSaveError(null);
      onSaved(woIds);
      onClose();
    },
    onError: (e) => {
      setSaveError(e instanceof Error ? e.message : 'Gagal menyimpan.');
    },
  });

  const busy = saveM.isPending || reviewM.isPending;

  const titleJob =
    row?.work_order_job_number ||
    formsByWo.get(row?.work_order_document_id ?? '')?.work_order_job_number ||
    row?.work_order_document_id ||
    '';

  const titleParam = row?.parameter_name?.trim() || 'Parameter';

  const dialogTitle =
    mode === 'parameter' ? (
      <>
        Isi Hasil Uji — <ChemParameterLabel nama={titleParam} simbol={row?.parameter_simbol} />
      </>
    ) : (
      <>Isi Hasil Uji — {titleJob || '…'} | grouped by Job Number</>
    );

  const canSave =
    formsReady &&
    (mode === 'workOrder' ? workOrderSections.length > 0 : parameterEntries.length > 0);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) onClose();
        }}
        fullWidth
        maxWidth="md"
        data-testid="laboratory-result-edit-dialog"
      >
        <DialogTitle sx={{ '& .katex-html': { whiteSpace: 'normal' } }}>
          {dialogTitle}
          {mode === 'workOrder' && anchorJenisSampel ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
              Jenis sampel: {anchorJenisSampel}
            </Typography>
          ) : null}
          {mode === 'parameter' ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
              Parameter yang sama di beberapa titik / job number
            </Typography>
          ) : null}
          {canSpvReview ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Isian Approved terkunci. Gunakan Request edit (atau Reject) untuk membuka lagi.
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {formsPending && !formsReady ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : null}
          {formsError ? (
            <Alert severity="error">
              {formsError instanceof Error ? formsError.message : 'Gagal memuat form.'}
            </Alert>
          ) : null}
          {!formsPending && !formsError && !canSave ? (
            <Alert severity="warning">Data hasil uji untuk baris ini tidak ditemukan.</Alert>
          ) : null}
          {saveError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          ) : null}
          {actionError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          ) : null}
          {actionSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
              {actionSuccess}
            </Alert>
          ) : null}

          {mode === 'workOrder' && workOrderSections.length > 0 ? (
            <Stack spacing={2.5} sx={{ pt: 0.5 }}>
              {workOrderSections.map((ba, idx) => {
                const jenis = jenisSampelLabel(ba, anchorJenisSampel);
                const pendingFields: React.ReactNode[] = [];
                const approvedFields: React.ReactNode[] = [];
                for (const p of ba.parameters) {
                  if (
                    lockedAnalisType != null &&
                    !analisTypeListMatches(p.analis_types, lockedAnalisType)
                  ) {
                    continue;
                  }
                  const key = resultKey(ba.berita_acara_document_id, p.parameter_uji_document_id);
                  const focused =
                    ba.berita_acara_document_id === focusBaDocId &&
                    p.parameter_uji_document_id === focusParamId;
                  const reviewStatus =
                    paramMetaByKey.get(key)?.review_status ?? p.review_status ?? 'pending';
                  const field = (
                    <ResultFieldWithReviewActions
                      key={key}
                      fieldKey={key}
                      focused={focused}
                      reviewStatus={reviewStatus}
                      canSpvReview={canSpvReview}
                      busy={busy}
                      value={results[key] ?? ''}
                      showCorrected={Boolean(ba.has_emission_correction)}
                      correctedValue={correctedResults[key] ?? ''}
                      onChange={(v) => handleResultChange(key, v)}
                      onCorrectedChange={(v) => handleCorrectedChange(key, v)}
                      fieldParameter={
                        showFieldParameter && p.is_parameter_lapangan
                          ? { value: fieldParameterValues[key] ?? '', readOnly: !canEditFieldParameter }
                          : null
                      }
                      onFieldParameterChange={(v) => handleFieldParameterChange(key, v)}
                      onApprove={() => approveParam(key)}
                      onRejectOrRequestEdit={() =>
                        openRejectOrRequestEdit(
                          key,
                          reviewStatus === 'approved' ? 'request_edit' : 'reject',
                        )
                      }
                      label={
                        <ParameterLabel
                          name={p.parameter_name}
                          simbol={p.parameter_simbol}
                          focused={focused}
                          reviewStatus={reviewStatus}
                        />
                      }
                    />
                  );
                  if (reviewStatus === 'approved') approvedFields.push(field);
                  else pendingFields.push(field);
                }
                return (
                  <Box key={ba.berita_acara_document_id}>
                    {idx > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      {ba.point_label}
                      {ba.location_name ? ` · ${ba.location_name}` : ''}
                    </Typography>
                    {jenis ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1.25 }}
                      >
                        Jenis sampel: {jenis}
                      </Typography>
                    ) : (
                      <Box sx={{ mb: 1 }} />
                    )}
                    <SecondaryDataButton
                      lhuGroupedName={ba.lhu_grouped_name}
                      testId={`laboratory-result-secondary-data-${ba.berita_acara_document_id}`}
                      onClick={() =>
                        setSecondaryTarget({
                          workOrderDocumentId: row?.work_order_document_id?.trim() ?? '',
                          beritaAcaraDocumentId: ba.berita_acara_document_id,
                          lhuGroupedName: ba.lhu_grouped_name ?? '',
                          html: ba.secondary_data_html ?? '',
                        })
                      }
                    />
                    <Stack spacing={1.5}>
                      {pendingFields}
                      {approvedFields.length > 0 ? (
                        <ApprovedResultsSection
                          sectionKey={ba.berita_acara_document_id}
                          expanded={Boolean(expandedSections[ba.berita_acara_document_id])}
                          onToggle={toggleSection}
                        >
                          {approvedFields}
                        </ApprovedResultsSection>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          {mode === 'parameter' && parameterEntries.length > 0 ? (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              {(() => {
                const pendingFields: React.ReactNode[] = [];
                const approvedFields: React.ReactNode[] = [];
                for (const entry of parameterEntries) {
                  const reviewStatus =
                    paramMetaByKey.get(entry.key)?.review_status ?? entry.review_status;
                  const field = (
                    <ResultFieldWithReviewActions
                      key={entry.key}
                      fieldKey={entry.key}
                      focused={entry.focused}
                      reviewStatus={reviewStatus}
                      canSpvReview={canSpvReview}
                      busy={busy}
                      value={results[entry.key] ?? ''}
                      showCorrected={entry.has_emission_correction}
                      correctedValue={correctedResults[entry.key] ?? ''}
                      onChange={(v) => handleResultChange(entry.key, v)}
                      onCorrectedChange={(v) => handleCorrectedChange(entry.key, v)}
                      fieldParameter={
                        showFieldParameter && entry.is_parameter_lapangan
                          ? {
                              value: fieldParameterValues[entry.key] ?? '',
                              readOnly: !canEditFieldParameter,
                            }
                          : null
                      }
                      onFieldParameterChange={(v) => handleFieldParameterChange(entry.key, v)}
                      onApprove={() => approveParam(entry.key)}
                      onRejectOrRequestEdit={() =>
                        openRejectOrRequestEdit(
                          entry.key,
                          reviewStatus === 'approved' ? 'request_edit' : 'reject',
                        )
                      }
                      label={
                        <Box>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: entry.focused ? 600 : 500,
                                '& .katex-html': { whiteSpace: 'normal' },
                              }}
                            >
                              {entry.point_label}
                            </Typography>
                            {entry.analis_types.length > 0 ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="info"
                                label={`Analis: ${entry.analis_types.join(', ')}`}
                                data-testid={`laboratory-result-edit-analis-type-${entry.key}`}
                              />
                            ) : null}
                            <SecondaryDataButton
                              inline
                              lhuGroupedName={entry.lhu_grouped_name}
                              testId={`laboratory-result-secondary-data-${entry.key}`}
                              onClick={() =>
                                setSecondaryTarget({
                                  workOrderDocumentId: entry.work_order_document_id,
                                  beritaAcaraDocumentId: entry.berita_acara_document_id,
                                  lhuGroupedName: entry.lhu_grouped_name,
                                  html: entry.secondary_data_html,
                                })
                              }
                            />
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              '& .katex-html': { whiteSpace: 'normal' },
                              '& sub, & sup': { fontSize: '0.75em', lineHeight: 0 },
                            }}
                          >
                            <ChemParameterLabel
                              nama={entry.parameter_name}
                              simbol={entry.parameter_simbol}
                            />
                            {entry.jenis_sampel || entry.location_name
                              ? ` · ${[
                                  entry.jenis_sampel ? `Jenis sampel: ${entry.jenis_sampel}` : '',
                                  entry.location_name,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}`
                              : ''}
                          </Typography>
                          <Chip
                            size="small"
                            sx={{ mt: 0.5 }}
                            color={reviewChipColor(reviewStatus)}
                            label={reviewChipLabel(reviewStatus)}
                          />
                        </Box>
                      }
                    />
                  );
                  if (reviewStatus === 'approved') approvedFields.push(field);
                  else pendingFields.push(field);
                }
                return (
                  <>
                    {pendingFields}
                    {approvedFields.length > 0 ? (
                      <ApprovedResultsSection
                        sectionKey="parameter"
                        expanded={Boolean(expandedSections.parameter)}
                        onToggle={toggleSection}
                      >
                        {approvedFields}
                      </ApprovedResultsSection>
                    ) : null}
                  </>
                );
              })()}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={busy || !canSave}
            onClick={() => {
              setSaveError(null);
              saveM.mutate();
            }}
            data-testid="laboratory-result-edit-save"
          >
            {saveM.isPending ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>

      <SecondaryDataHtmlDialog
        open={Boolean(secondaryTarget)}
        beritaAcaraDocumentId={secondaryTarget?.beritaAcaraDocumentId ?? ''}
        lhuGroupedName={secondaryTarget?.lhuGroupedName ?? ''}
        storedHtml={secondaryTarget?.html ?? ''}
        onClose={() => {
          if (!secondaryTarget) return;
          setSecondaryTarget(null);
        }}
        onSaved={(html) => {
          const target = secondaryTarget;
          if (!target || !jwt) return;
          queryClient.setQueryData<LaboratoryResultWorkOrderFormData>(
            queryKeys.laboratoryResultWorkOrderForm(jwt, target.workOrderDocumentId),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                berita_acara: old.berita_acara.map((entry) =>
                  entry.berita_acara_document_id === target.beritaAcaraDocumentId
                    ? { ...entry, secondary_data_html: html }
                    : entry,
                ),
              };
            },
          );
          setSecondaryTarget((current) =>
            current && current.beritaAcaraDocumentId === target.beritaAcaraDocumentId
              ? { ...current, html }
              : current,
          );
        }}
      />

      <Dialog
        open={Boolean(rejectDialog)}
        onClose={() => {
          if (!reviewM.isPending) setRejectDialog(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {rejectDialog?.mode === 'request_edit' ? 'Request edit parameter?' : 'Reject parameter?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {rejectDialog?.mode === 'request_edit'
              ? 'Parameter yang sudah Approved akan di-reject agar isian bisa diubah lagi (request edit).'
              : 'Tolak hasil parameter ini? Analis dapat mengisi ulang setelah reject.'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 1.5, '& .katex-html': { whiteSpace: 'normal' } }}
          >
            <ChemParameterLabel nama={rejectDialog?.paramName} />
          </Typography>
          <TextField
            label="Catatan (opsional)"
            fullWidth
            multiline
            minRows={2}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            inputProps={{ 'data-testid': 'laboratory-result-edit-reject-note' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)} disabled={reviewM.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            color={rejectDialog?.mode === 'request_edit' ? 'warning' : 'error'}
            disabled={reviewM.isPending || !rejectDialog}
            data-testid="laboratory-result-edit-reject-confirm"
            onClick={() => {
              if (!rejectDialog) return;
              const meta = paramMetaByKey.get(rejectDialog.key);
              if (!meta) return;
              reviewM.mutate({
                work_order_document_id: meta.work_order_document_id,
                berita_acara_document_id: meta.berita_acara_document_id,
                parameter_uji_document_id: meta.parameter_uji_document_id,
                action: 'reject',
                review_note: rejectNote.trim() || undefined,
              });
            }}
          >
            {reviewM.isPending
              ? 'Memproses…'
              : rejectDialog?.mode === 'request_edit'
                ? 'Request edit'
                : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
